/* STATE */
import { call, put, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { AppAction, createActionType, createApiActionCreators, RequestActionTypes } from 'helpers/redux/reduxHelpers';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { setCurrentStepAction } from 'saga/billingManagement/claims/ducks';

export interface ClaimStep4Payload {
  invoiceNumber?: string;
  invoiceNumberNew?: string;
  claimId?: number;
  serviceId?: number;
  fid?: number;
  file?: File;
  fromSummary?: boolean;
}

/* ACTION TYPES */
export enum ClaimStep4StateTypes {
  GetData = '@@ClaimStep4/GET_DATA',
  SaveClaimStep4 = '@@ClaimStep4/SAVE_CLAIM_STEP_4',
  DownloadFileStep4 = '@@ClaimStep4/DOWNLOAD_FILE_CLAIM_STEP_4',
  RemoveFileStep4 = '@@ClaimStep4/REMOVE_FILE_CLAIM_STEP_4',
  UploadFileStep4 = '@@ClaimStep4/UPLOAD_FILE_CLAIM_STEP_4',
}

/* ACTIONS */
export const getClaimStep4DataActions = createApiActionCreators(ClaimStep4StateTypes.GetData);
export const saveClaimStep4Actions = createApiActionCreators(ClaimStep4StateTypes.SaveClaimStep4);
export const downloadFileStep4Actions = createApiActionCreators(ClaimStep4StateTypes.DownloadFileStep4);
export const removeFileStep4Actions = createApiActionCreators(ClaimStep4StateTypes.RemoveFileStep4);
export const uploadFileStep4Actions = createApiActionCreators(ClaimStep4StateTypes.UploadFileStep4);

/* SAGAS */

function* getData({ payload }: AppAction<ClaimStep4Payload>) {
  if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.getData,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getClaimStep4DataActions.success(resp.data));
  } else {
    yield put(getClaimStep4DataActions.failure());
  }
}

function* saveClaimStep4({ payload }: AppAction<ClaimStep4Payload>) {
  if (!payload.invoiceNumber || !payload.claimId) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.saveClaimStep4,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    if (payload.fromSummary) {
      yield put(
        setCurrentStepAction({
          step: 6,
          invoiceNumber: payload.invoiceNumber,
          claimId: payload.claimId,
          serviceId: payload.serviceId,
        })
      );
    } else {
      yield put(saveClaimStep4Actions.success());
    }
  } else {
    yield put(saveClaimStep4Actions.failure());
  }
}

function downloadStepFile({ payload }: AppAction<ClaimStep4Payload>) {
  const url = environment.properties[environment.urlConfig.billingManagement.propertyKey];
  const link = document.createElement('a');

  link.target = '_blank';
  link.href = `${url}/claimStep4/downloadFileGet?invoicenumber=${payload.invoiceNumber}&claimid=${payload.claimId}&serviceid=${payload.serviceId}&fid=${payload.fid}`;
  link.click();
  link.remove();
}

/*
 * function* downloadStepFile({ payload }: AppAction<ClaimStep4Payload>) {
 *   if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId || !payload.fid) return;
 *   const request: ApiClientRequest = {
 *     apiCall: call(
 *       api.downloadFile,
 *       yield getApiUrl(environment.urlConfig.billingManagement),
 *       payload.invoiceNumber,
 *       payload.claimId,
 *       payload.serviceId,
 *       payload.fid
 *     ),
 *   };
 *   const resp: ExtendedAxiosResponse = yield call(performApiCall, request);
 *   console.log('debug of response status for download file', resp.status);
 *   if (resp.ok) {
 *     console.log('debug of response visible headers in axios for donwload file', resp.headers);
 *     let contentDispositionString = 'Content-Disposition';
 *     if (!resp.headers[contentDispositionString]) {
 *       contentDispositionString = 'content-disposition';
 *     }
 *
 *     if (!resp.headers[contentDispositionString]) {
 *       console.log(
 *         'response header content-disposition was not found - killed saga downloadStepFile headers:',
 *         resp.headers
 *       );
 *       return;
 *     }
 *     const type = resp.headers['Content-Type'];
 *     const [, filename] = resp.headers[contentDispositionString]?.split('filename=');
 *     const byteCharacters = window.atob(resp.data);
 *     const byteNumbers = new Array(byteCharacters.length);
 *     for (let i = 0; i < byteCharacters.length; i++) {
 *       byteNumbers[i] = byteCharacters.charCodeAt(i);
 *     }
 *     const url = URL.createObjectURL(new Blob([new Uint8Array(byteNumbers)], { type: type }));
 *     const link = document.createElement('a');
 *     link.target = '_blank';
 *     link.href = url;
 *     link.download = removeDoubleQuotes(decodeURIComponent(filename));
 *     link.click();
 *     link.remove();
 *
 *     yield put(downloadFileStep4Actions.success());
 *     yield put(getClaimStep4DataActions.request(payload));
 *   } else {
 *     yield put(downloadFileStep4Actions.failure());
 *   }
 * }
 */

function* removeFile({ payload }: AppAction<ClaimStep4Payload>) {
  if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId || !payload.fid) return;

  const request: ApiClientRequest = {
    apiCall: call(
      api.removeFile,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      payload.fid
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(removeFileStep4Actions.success());
    yield put(getClaimStep4DataActions.request(payload));
  } else {
    yield put(removeFileStep4Actions.failure());
  }
}

function* uploadFile({ payload }: AppAction<ClaimStep4Payload>) {
  if (!payload.invoiceNumber || !payload.claimId || !payload.serviceId || !payload.file) return;

  const formData = new FormData();

  formData.append('file', payload.file, payload.file.name);

  const request: ApiClientRequest = {
    apiCall: call(
      api.uploadFile,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId,
      formData
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(uploadFileStep4Actions.success(resp?.data));
  } else {
    yield put(uploadFileStep4Actions.failure());
  }
}

/* EXPORT */
export function* claimStep4Saga() {
  yield takeLatest(createActionType(ClaimStep4StateTypes.GetData, RequestActionTypes.REQUEST), getData);
  yield takeLatest(createActionType(ClaimStep4StateTypes.SaveClaimStep4, RequestActionTypes.REQUEST), saveClaimStep4);
  yield takeLatest(
    createActionType(ClaimStep4StateTypes.DownloadFileStep4, RequestActionTypes.REQUEST),
    downloadStepFile
  );
  yield takeLatest(createActionType(ClaimStep4StateTypes.RemoveFileStep4, RequestActionTypes.REQUEST), removeFile);
  yield takeLatest(createActionType(ClaimStep4StateTypes.UploadFileStep4, RequestActionTypes.REQUEST), uploadFile);
}
